import React, { useState } from 'react';
import { products } from '../../data/products';
import { useReveal } from '../../hooks/useReveal';
import mark from '../../images/evolve-mark.png';
import './Constellation.scss';

const W = 1000;
const H = 620;
const CX = W / 2;
const CY = H / 2;
const RX = 372;
const RY = 224;

// Distribute the products around an ellipse, starting at the top.
const nodes = products.map((p, i) => {
    const angle = (i / products.length) * Math.PI * 2 - Math.PI / 2;
    return {
        ...p,
        x: CX + Math.cos(angle) * RX,
        y: CY + Math.sin(angle) * RY,
    };
});

const Constellation = () => {
    const [hovered, setHovered] = useState(null);
    const [ref, revealed] = useReveal({ threshold: 0.2 });
    const active = nodes.find((n) => n.id === hovered);

    return (
        <section className="constellation" id="ecosystem" ref={ref}>
            <div className="cn-head">
                <span className="cn-eyebrow">The Ecosystem</span>
                <h2 className="cn-title">One lab. Many products.</h2>
                <p className="cn-sub">
                    Every product below is designed, owned, and operated by Evolve Labs.
                </p>
            </div>

            <div className={`cn-stage ${revealed ? 'is-in' : ''}`}>
                <svg
                    className="cn-svg"
                    viewBox={`0 0 ${W} ${H}`}
                    preserveAspectRatio="xMidYMid meet"
                    role="img"
                    aria-label="Evolve Labs product ecosystem"
                >
                    <defs>
                        <radialGradient id="cn-core-glow">
                            <stop offset="0%" stopColor="#EF002F" stopOpacity="0.35" />
                            <stop offset="100%" stopColor="#EF002F" stopOpacity="0" />
                        </radialGradient>
                    </defs>

                    {/* Connective tissue */}
                    <g className="cn-links">
                        {nodes.map((n, i) => (
                            <line
                                key={n.id}
                                x1={CX}
                                y1={CY}
                                x2={n.x}
                                y2={n.y}
                                className={`cn-link ${hovered === n.id ? 'is-hot' : ''} ${
                                    hovered && hovered !== n.id ? 'is-dim' : ''
                                }`}
                                style={{ '--i': i, '--accent': n.accent }}
                            />
                        ))}
                    </g>

                    {/* Core — the mark sits at the centre of the ecosystem */}
                    <circle cx={CX} cy={CY} r={150} fill="url(#cn-core-glow)" />
                    <circle cx={CX} cy={CY} r={62} className="cn-core-ring" />
                    <image
                        href={mark}
                        x={CX - 44}
                        y={CY - 44}
                        width={88}
                        height={88}
                        className="cn-core-mark"
                    />
                    <text x={CX} y={CY + 92} className="cn-core-label" textAnchor="middle">
                        EVOLVE LABS
                    </text>

                    {/* Product nodes */}
                    <g className="cn-nodes">
                        {nodes.map((n, i) => {
                            const rightSide = n.x > CX;
                            return (
                                <g
                                    key={n.id}
                                    className={`cn-node ${hovered === n.id ? 'is-hot' : ''} ${
                                        hovered && hovered !== n.id ? 'is-dim' : ''
                                    }`}
                                    style={{ '--i': i, '--accent': n.accent }}
                                    onMouseEnter={() => setHovered(n.id)}
                                    onMouseLeave={() => setHovered(null)}
                                    tabIndex={0}
                                    onFocus={() => setHovered(n.id)}
                                    onBlur={() => setHovered(null)}
                                >
                                    <circle cx={n.x} cy={n.y} r={26} className="cn-node-hit" />
                                    <circle cx={n.x} cy={n.y} r={13} className="cn-node-halo" />
                                    <circle cx={n.x} cy={n.y} r={6} className="cn-node-dot" />
                                    <text
                                        x={rightSide ? n.x + 24 : n.x - 24}
                                        y={n.y + 4}
                                        textAnchor={rightSide ? 'start' : 'end'}
                                        className="cn-node-label"
                                    >
                                        {(n.short || n.name).toUpperCase()}
                                    </text>
                                </g>
                            );
                        })}
                    </g>
                </svg>

                {/* Readout panel */}
                <div className={`cn-readout ${active ? 'is-on' : ''}`} aria-live="polite">
                    {active ? (
                        <>
                            <span className="cn-readout-name" style={{ color: active.accent }}>
                                {active.name}
                            </span>
                            <dl className="cn-readout-grid">
                                <div>
                                    <dt>Category</dt>
                                    <dd>{active.category}</dd>
                                </div>
                                <div>
                                    <dt>Status</dt>
                                    <dd>{active.status}</dd>
                                </div>
                                <div>
                                    <dt>Platform</dt>
                                    <dd>{active.platform}</dd>
                                </div>
                                <div>
                                    <dt>Year</dt>
                                    <dd>{active.year}</dd>
                                </div>
                            </dl>
                        </>
                    ) : (
                        <span className="cn-readout-idle">
                            Hover a node to inspect the product
                        </span>
                    )}
                </div>

                {/* Compact ecosystem readout for narrow screens */}
                <ul className="cn-mobile-list">
                    {nodes.map((n) => (
                        <li key={n.id} style={{ '--accent': n.accent }}>
                            <span className="cn-ml-dot" aria-hidden="true" />
                            <span className="cn-ml-name">{n.short || n.name}</span>
                            <span className="cn-ml-cat">{n.category}</span>
                            <span className="cn-ml-status">{n.status}</span>
                        </li>
                    ))}
                </ul>
            </div>
        </section>
    );
};

export default Constellation;
